import React, { useState, useEffect, useRef } from 'react';
import { ApiService } from '../services/api';
import { Image, Upload } from 'lucide-react';

const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            const img = new window.Image();
            img.onload = () => {
                const MAX_SIZE = 1920;
                let width = img.width;
                let height = img.height;
                if (width > height) {
                    if (width > MAX_SIZE) { height = Math.round((height * MAX_SIZE) / width); width = MAX_SIZE; }
                } else {
                    if (height > MAX_SIZE) { width = Math.round((width * MAX_SIZE) / height); height = MAX_SIZE; }
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', 0.85));
            };
            img.onerror = reject;
            img.src = e.target?.result as string;
        };
        reader.onerror = reject;
    });
};

interface DBFile {
    id: string;
    name: string;
    mime_type: string;
    blob_url: string;
}

interface MediaGalleryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (url: string) => void;
}

const MediaGalleryModal: React.FC<MediaGalleryModalProps> = ({ isOpen, onClose, onSelect }) => {
    const [files, setFiles] = useState<DBFile[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            loadFiles();
        }
    }, [isOpen]);

    const loadFiles = async () => {
        setLoading(true);
        try {
            const list = await ApiService.getFiles();
            // Filter images only
            setFiles(list.filter((f: DBFile) => f.mime_type.startsWith('image/')));
        } catch (e) {
            console.error('Failed to load media files', e);
        }
        setLoading(false);
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const base64 = await resizeImage(file);
            await ApiService.uploadFile(file.name, 'image/jpeg', base64);
            await loadFiles();
        } catch (err: any) {
            console.error('Upload failed', err);
            alert(`Upload fehlgeschlagen: ${err?.message || JSON.stringify(err)}`);
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-5xl h-[80vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-slate-700">
                    <div>
                        <h2 className="text-xl font-black text-white uppercase tracking-wider">Mediendatenbank</h2>
                        <p className="text-xs text-slate-400">Wähle ein Bild aus oder lade ein neues hoch.</p>
                    </div>
                    <div className="flex gap-4 items-center">
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                            accept="image/*"
                            title="Bild hochladen"
                        />
                        <button
                            onClick={handleUploadClick}
                            disabled={uploading}
                            className={`bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-lg transition-all ${uploading ? 'opacity-50 cursor-wait' : ''}`}
                        >
                            {uploading ? 'Lädt hoch...' : '<Upload className="w-4 h-4 inline mr-1" /> Bild hochladen'}
                        </button>
                        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors text-2xl">✕</button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {loading ? (
                        <div className="flex justify-center items-center h-full text-slate-500">Lade Bilder...</div>
                    ) : files.length === 0 ? (
                        <div className="flex flex-col justify-center items-center h-full text-slate-500">
                            <span className="text-4xl mb-4"><Image className="w-4 h-4 inline mr-1" /></span>
                            <p>Keine Bilder gefunden.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {files.map(f => (
                                <div
                                    key={f.id}
                                    className="group relative aspect-square bg-slate-800 rounded-xl overflow-hidden border border-slate-700 hover:border-sky-500 cursor-pointer transition-all"
                                    onClick={() => {
                                        onSelect(f.blob_url);
                                        onClose();
                                    }}
                                >
                                    <img src={f.blob_url} alt={f.name} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all" />
                                    <div className="absolute inset-x-0 bottom-0 bg-black/60 backdrop-blur-sm p-2 translate-y-full group-hover:translate-y-0 transition-transform">
                                        <p className="text-[10px] text-white truncate font-mono">{f.name}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MediaGalleryModal;
